import { Device } from '@capacitor/device';
import { useState } from 'react';

import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import { isIOS } from 'pkg/platform';

type KnownBrowserEngine = 'safari' | 'chrome' | 'firefox';

type BrowserEngine = KnownBrowserEngine | 'unknown';

const UnknownEngine: BrowserEngine = 'unknown';
const UnknownVersion: string = '0.000';

/**
 *	List of the minimum version we currently support,
 *	this list needs to be vetted and maintained.
 */
export const SupportedBrowserEngineVersions: Record<
	KnownBrowserEngine,
	string
> = {
	safari: '16.4',
	chrome: '122.0',
	firefox: '122.0',
};

/**
 *	Version number is padded into int.
 *	Minimum required version is 16.4
 */
const MinimumIOSVersionNumber: number = 160400;

interface ClientBrowserInfo {
	engine: BrowserEngine;
	version: string;
}

export function getClientBrowserInfo(userAgent: string): ClientBrowserInfo {
	const ua = userAgent.replace(/^mozilla\/\d\.\d\W/i, '');

	let engine: BrowserEngine = UnknownEngine;
	let version: string = UnknownVersion;

	const vendorAndVersionRegex = /(\w+)\/(\d+\.\d+(?:\.\d+)?(?:\.\d+)?)/gi;
	const versionRegex = /version\/(\d+(\.\d+)*)/i;

	const getSafariVersion = (userAgent: string): string => {
		// Safari version is defined as "Version/<version>", and then Safari/<build>
		const safariVersionMatch = userAgent.match(versionRegex);
		const safariVersion = Array.isArray(safariVersionMatch)
			? safariVersionMatch[1]
			: null;

		return safariVersion;
	};

	const vendorMatches = ua.match(vendorAndVersionRegex);

	if (vendorMatches) {
		const lastVendor = vendorMatches?.pop();
		let [vendorName, vendorVersion] = lastVendor?.split('/');

		// Chrome based browsers don't have "Chrome" as the last vendor.
		const chromeVendor = vendorMatches.find((vendor) =>
			vendor.toLowerCase().startsWith('chrome/')
		);
		if (chromeVendor) {
			[vendorName, vendorVersion] = chromeVendor.split('/');
		}

		switch (vendorName) {
			case 'Firefox':
			case 'Chrome':
				engine = vendorName.toLocaleLowerCase() as BrowserEngine;
				version = vendorVersion;
				break;
			case 'Safari':
				engine = 'safari';
				version = getSafariVersion(ua);
				break;
		}

		/** Capture chromium based browsers. (I don't know why Edge spells it Edg) */
		if (engine === UnknownEngine || vendorName === 'Edg') {
			const chromiumUserAgent = vendorMatches.find((vendor) =>
				vendor.toLocaleLowerCase().includes('chrome/')
			);

			if (chromiumUserAgent) {
				engine = 'chrome';
				version = chromiumUserAgent.replace('Chrome/', '');
			}
		}
	}

	return {
		engine,
		version,
	};
}

export function useIsBrowserSupported(): boolean {
	const [isSupported, setSupported] = useState<boolean>(true);

	useComponentDidMount(async () => {
		if (isIOS()) {
			const iosSupported = await isSupportedIOSVersion();

			setSupported(iosSupported);
		} else {
			setSupported(isBrowserSupported(navigator.userAgent));
		}
	});

	return isSupported;
}

export function isBrowserSupported(userAgent: string): boolean {
	const { engine, version } = getClientBrowserInfo(userAgent);

	// When engine is unknown, we'll just let it go through
	if (engine === 'unknown') {
		return true;
	}

	// Asserts that a > b for version numbers
	const versionCompare = (a: string, b: string): boolean =>
		a && b && a.localeCompare(b, undefined, { numeric: true }) === 1;

	const minimumSupportedByEngine = SupportedBrowserEngineVersions[engine];

	return versionCompare(version, minimumSupportedByEngine);
}

async function isSupportedIOSVersion(): Promise<boolean> {
	if (isIOS()) {
		const info = await Device.getInfo();

		return info.iOSVersion > MinimumIOSVersionNumber;
	}

	return true;
}
